import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import type { DocAccess, DocFeature, DocRelease } from "@xop/dashboard-shared";

import { MultiSelect } from "../../../components/ui/multi-select.js";
import { listDocFeatures, listDocProducts } from "../api.js";
import type { FormState } from "./formState.js";

/**
 * Who a document is FOR — the library-wide decision.
 *
 * This is the control that was missing. `access_scope`, `document_products`
 * and `document_releases` have existed in portal-api since May and the
 * customer gate honours them, but no editor ever sent the fields, so every
 * document in production is `general` and the only reachable lever was a
 * per-folder toggle that applies to every customer at once. That is the
 * whole reason "the customer who bought Mass Notification sees the Webcast
 * manual" was unfixable.
 *
 * Two axes, deliberately separate and both needed:
 *
 *   HERE (the document)  "this manual is for the USN product" — one
 *                        statement that scales to every account that owns
 *                        it, and the thing you want to be true by default.
 *
 *   Documentation ▸ Access (the account)  "…except this customer, who also
 *                        gets it" — the exception, per account.
 *
 * Scope is explicit rather than inferred from what is tagged, so a USN
 * brochure that is genuinely public marketing can stay `general` with the
 * product still tagged for search and filtering. Inferring would take that
 * choice away.
 *
 * `feature` (2026-09-12) is one level finer than `product`: "customers who
 * own Mass Notification" rather than "customers who own USN". The options
 * come from the CRM's per-product feature catalog; a reseller reaches a
 * feature-scoped document through the feature's owning product.
 */
export function featureOptionLabel(f: DocFeature): string {
  return f.product_name ? `${f.product_name} · ${f.name} · ${f.slug}` : `${f.name} · ${f.slug}`;
}

export function DocAccessPanel({
  form,
  setForm,
  releases,
  initialAccess,
}: {
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
  releases: DocRelease[];
  /** The document's stored access, when editing. Used only to explain what
   * a `private` document is currently bound to — reassigning the owning
   * customer is a Documentation ▸ Access decision, not an editing one. */
  initialAccess?: DocAccess | undefined;
}): JSX.Element {
  const productsQuery = useQuery({
    queryKey: ["docs", "products"],
    queryFn: listDocProducts,
  });
  const featuresQuery = useQuery({
    queryKey: ["docs", "features"],
    queryFn: listDocFeatures,
  });

  const products = productsQuery.data?.data ?? [];
  const features = featuresQuery.data?.data ?? [];
  const scope = form.access_scope;

  return (
    <Card variant="outlined">
      <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box>
          <Typography variant="subtitle2">Who this document is for</Typography>
          <Typography variant="caption" color="text.secondary">
            Applies to every account. Per-customer exceptions live in Documentation ▸ Access.
          </Typography>
        </Box>

        <TextField
          select
          size="small"
          label="Audience"
          value={scope}
          onChange={(e) =>
            setForm((s) => ({ ...s, access_scope: e.target.value as DocAccess["scope"] }))
          }
          inputProps={{ "data-testid": "doc-access-scope" }}
        >
          <MenuItem value="general">Everyone — ungated (whitepapers, press, marketing)</MenuItem>
          <MenuItem value="product">Customers who own a product</MenuItem>
          <MenuItem value="feature">Customers who own a feature (e.g. Mass Notification)</MenuItem>
          <MenuItem value="release">Customers on a release</MenuItem>
          <MenuItem value="private">One customer only</MenuItem>
        </TextField>

        {scope === "product" ? (
          <Box>
            <Typography variant="caption" color="text.secondary" component="label">
              Products
            </Typography>
            <MultiSelect
              options={products.map((p) => ({ value: p.code, label: `${p.name} · ${p.code}` }))}
              selected={form.product_codes}
              onChange={(next) => setForm((s) => ({ ...s, product_codes: next }))}
              placeholder={productsQuery.isLoading ? "Loading products…" : "Pick one or more products"}
              emptyMessage="No products have synced from the CRM yet."
            />
          </Box>
        ) : null}

        {scope === "feature" ? (
          <Box>
            <Typography variant="caption" color="text.secondary" component="label">
              Features
            </Typography>
            <MultiSelect
              options={features.map((f) => ({ value: f.slug, label: featureOptionLabel(f) }))}
              selected={form.feature_codes}
              onChange={(next) => setForm((s) => ({ ...s, feature_codes: next }))}
              placeholder={featuresQuery.isLoading ? "Loading features…" : "Pick one or more features"}
              emptyMessage="No features have synced from the CRM yet."
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              A customer sees this when one of these features is switched on for them. A reseller
              sees it when it sells the feature’s product.
            </Typography>
          </Box>
        ) : null}

        {scope === "release" ? (
          <Box>
            <Typography variant="caption" color="text.secondary" component="label">
              Releases
            </Typography>
            <MultiSelect
              options={releases.map((r) => ({ value: String(r.id), label: r.version }))}
              selected={form.release_ids.map(String)}
              onChange={(next) => setForm((s) => ({ ...s, release_ids: next.map(Number) }))}
              placeholder="Pick one or more releases"
              emptyMessage="No releases yet."
            />
          </Box>
        ) : null}

        {scope === "private" ? (
          <Alert severity="info" sx={{ py: 0.5 }}>
            {initialAccess?.private_to_org ? (
              <>
                Only <strong>{initialAccess.private_to_org.name}</strong> can read this. Change the
                owner from Documentation ▸ Access.
              </>
            ) : (
              <>
                Pick the owning customer on Documentation ▸ Access after saving. Until then this
                document reaches nobody but staff.
              </>
            )}
          </Alert>
        ) : null}

        {scope === "product" && form.product_codes.length === 0 ? (
          <Alert severity="warning" sx={{ py: 0.5 }}>
            No product tagged yet — this document currently reaches no customer.
          </Alert>
        ) : null}

        {scope === "feature" && form.feature_codes.length === 0 ? (
          <Alert severity="warning" sx={{ py: 0.5 }}>
            No feature tagged yet — this document currently reaches no customer.
          </Alert>
        ) : null}

        {scope === "general" ? (
          <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", flexWrap: "wrap" }}>
            <Chip size="small" label="Ungated" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
            <Typography variant="caption" color="text.secondary">
              Every customer whose folder is published sees this, whatever they bought.
            </Typography>
          </Box>
        ) : null}
      </CardContent>
    </Card>
  );
}
